import { Icon } from "@/components/icons/Icon";
import { useRedSurfacesSuppressed } from "@/components/admin/use-admin-surface";
import {
  CHALK_ADMIN_SURFACE_CLASS,
  CHALK_DANGER_SURFACE_CLASS,
} from "@/components/admin/admin-visual-treatment";
import {
  SideNavV2,
  SideNavV2Item,
  SideNavV2Section,
  SideNavWorkspaceAvatarV2,
  sideNavItemContainsId,
} from "@/components/interactive/SideNavV2";
import { ProjectNavbarProps } from "@/components/navigation/ProjectNavbar.types";
import { CollapsedNavbarAdminIndicators } from "@/components/navigation/CollapsedNavbarImpersonationIndicator";
import { ProjectNavbarAdminModeControl } from "@/components/navigation/ProjectNavbarAdminModeControl";
import { getProjectNavigationRouteKey } from "@/components/navigation/project-navigation-route-key";
import { setNavigationV2AppReturnPath } from "@/components/navigation/navigation-v2-return-path";
import {
  preserveOpenSidebarForNextRoute,
  useNavbarSmoothCollapse,
} from "@/components/navigation/use-navbar-smooth-collapse";
import { getSideNavPointerTargetId } from "@/components/navigation/project-nav-active-item";
import { useProjectSideNavModel } from "@/components/navigation/use-project-side-nav-model";
import { ZIndexClass } from "@/constants/z-index";
import { useGlobalAppState } from "@/utils/app-state";
import { classNames } from "@/utils/classnames";
import { preventDefaultAndStopPropagation } from "@/utils/event";
import { useRouteAndTeamInfo } from "@/utils/hooks/url/use-route-info";
import { usePersistentScrollTop } from "@/utils/hooks/use-persistent-scroll-position";
import { Routes } from "@/utils/routes";
import { isSidebarToggleShortcut } from "@/utils/sidebar-toggle-shortcut";
import { MOTION_DURATION_CLASS, MOTION_DURATION_MS } from "@/utils/motion";
import { useTheme } from "next-themes";
import { useRouter } from "next/router";
import {
  default as React,
  MouseEventHandler,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

// The rail's two motions: the content swap is short, the width is medium, so they read as a pair
// with every other panel in the shell.
const PROJECT_NAVBAR_CONTENT_TRANSITION_MS = MOTION_DURATION_MS.short;
const PROJECT_NAVBAR_WIDTH_TRANSITION_MS = MOTION_DURATION_MS.medium;
const PROJECT_NAVBAR_ROUTE_HANDOFF_TIMEOUT_MS = 5000;
export const PROJECT_NAVBAR_COLLAPSED_WIDTH_PX = 40;
export const PROJECT_NAVBAR_DEFAULT_WIDTH_PX = 200;
const PROJECT_NAVBAR_CONTENT_SWAP_CLASS =
  `absolute inset-y-0 left-0 transition-[opacity,transform] ${MOTION_DURATION_CLASS.short} ease-out ` +
  "will-change-transform motion-reduce:transition-none";
const PROJECT_NAVBAR_COLLAPSE_HOVER_SUPPRESSION_MS = PROJECT_NAVBAR_WIDTH_TRANSITION_MS + 50;
const PROJECT_NAVBAR_HOVER_MENU_TOGGLE_SUPPRESSION_MS = PROJECT_NAVBAR_WIDTH_TRANSITION_MS + 50;
interface HoverExpandedSidebarRouteHandoff {
  restore: boolean;
  collapseOnDestination: boolean;
}

// Module-level so client-side route changes into full-page views can animate from the prior shell.
let hasMountedProjectNavbarV2 = false;
let hoverExpandedSidebarRouteHandoff = {
  until: 0,
  collapseOnDestination: false,
};

const preserveHoverExpandedSidebarForNextRoute = ({
  collapseOnDestination = false,
}: { collapseOnDestination?: boolean } = {}) => {
  // Route changes can include page compilation/data loading, so this needs to cover the
  // navigation handoff rather than just the sidebar animation window.
  hoverExpandedSidebarRouteHandoff = {
    until: Date.now() + PROJECT_NAVBAR_ROUTE_HANDOFF_TIMEOUT_MS,
    collapseOnDestination,
  };
};

const getHoverExpandedSidebarRouteHandoff = (): HoverExpandedSidebarRouteHandoff => {
  const restore =
    typeof window !== "undefined" && Date.now() <= hoverExpandedSidebarRouteHandoff.until;

  return {
    restore,
    collapseOnDestination: restore && hoverExpandedSidebarRouteHandoff.collapseOnDestination,
  };
};

const clearHoverExpandedSidebarRouteHandoff = () => {
  hoverExpandedSidebarRouteHandoff = {
    until: 0,
    collapseOnDestination: false,
  };
};

const isEditableKeyboardTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;

  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    target.isContentEditable ||
    target.closest("[contenteditable='true']") != null ||
    target.closest("[role='textbox']") != null ||
    target.closest(".monaco-editor") != null ||
    target.closest(".cm-editor") != null
  );
};

const useTimedBooleanFlag = (durationMs: number) => {
  const [active, setActive] = React.useState(false);
  const timeoutRef = React.useRef<number | null>(null);

  const activate = React.useCallback(() => {
    if (timeoutRef.current != null) {
      window.clearTimeout(timeoutRef.current);
    }

    setActive(true);
    timeoutRef.current = window.setTimeout(() => {
      timeoutRef.current = null;
      setActive(false);
    }, durationMs);
  }, [durationMs]);

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current == null) return;

      window.clearTimeout(timeoutRef.current);
    };
  }, []);

  return [active, activate] as const;
};

export const ProjectNavbarV2: React.FC<ProjectNavbarProps> = ({
  activePage,
  activeSubPage,
  activePageV2,
  activeSubPageV2,
  defaultCollapsed = false,
  forceCollapsed = false,
  sideNavOverride,
}) => {
  const router = useRouter();
  const { resolvedTheme } = useTheme();
  const { state, updateState } = useGlobalAppState();
  const { environmentId, projectId } = useRouteAndTeamInfo();
  const {
    sections: effectiveSideNavSections,
    workspaceMenuItems: effectiveWorkspaceMenuItems,
    workspaceLabel: effectiveWorkspaceLabel,
    hasSideNavOverride,
    collapsedRouteItemIds,
    routeActiveItemId,
    settingsActive,
  } = useProjectSideNavModel({
    activePage,
    activeSubPage,
    activePageV2,
    activeSubPageV2,
    sideNavOverride,
  });
  const [hoverExpandedSidebarRouteHandoffOnMount] = React.useState(
    getHoverExpandedSidebarRouteHandoff,
  );
  const [collapsedRailHovered, setCollapsedRailHovered] = React.useState(
    hoverExpandedSidebarRouteHandoffOnMount.restore,
  );
  const [collapsedRailHoverSuppressed, setCollapsedRailHoverSuppressed] = React.useState(false);
  const routeRequestsCollapsedSidebar = defaultCollapsed || settingsActive;
  const [optimisticActiveItemId, setOptimisticActiveItemId] = useState<string | undefined>();
  const activeItemId = optimisticActiveItemId ?? routeActiveItemId;
  const sidebarToggleTooltipShortcut = activeItemId === "sql-explorer" ? "action" : "bracket";
  const themeMode =
    resolvedTheme === "dark" ? "dark" : resolvedTheme === "light" ? "light" : undefined;
  const hasCommittedRef = useRef(false);
  const defaultCollapsedRouteKey = getProjectNavigationRouteKey(router.asPath);
  const [defaultCollapsedAppliedRouteKey, setDefaultCollapsedAppliedRouteKey] = useState<string>();
  const shouldDeferDefaultCollapse =
    routeRequestsCollapsedSidebar &&
    !forceCollapsed &&
    state.sidebarOpen &&
    defaultCollapsedAppliedRouteKey !== defaultCollapsedRouteKey &&
    (hasCommittedRef.current || hasMountedProjectNavbarV2);
  const applyingDefaultCollapsed =
    routeRequestsCollapsedSidebar &&
    defaultCollapsedAppliedRouteKey !== defaultCollapsedRouteKey &&
    !shouldDeferDefaultCollapse;
  const setSidebarOpen = useCallback(
    (sidebarOpen: boolean) => updateState({ sidebarOpen }),
    [updateState],
  );
  const setUserSidebarOpen = React.useCallback(
    (sidebarOpen: boolean) => updateState({ sidebarOpen, sidebarOpenPreference: sidebarOpen }),
    [updateState],
  );
  const routeOpenHandoffActive = useNavbarSmoothCollapse({
    routeRequestsCollapsedSidebar,
    transitionMs: PROJECT_NAVBAR_CONTENT_TRANSITION_MS,
    setSidebarOpen,
  });
  const sidebarCollapsed =
    forceCollapsed || applyingDefaultCollapsed || !(state.sidebarOpen || routeOpenHandoffActive);
  const sideNavScrollPositionKey =
    sideNavOverride?.scrollPositionKey ??
    `project-nav-v2:${projectId ?? "unknown"}:${environmentId ?? "unknown"}`;
  const expandedPanelOpen = sidebarCollapsed && collapsedRailHovered;
  const sidebarLayoutExpanded = !sidebarCollapsed || expandedPanelOpen;
  const expandedPanelOpenRef = React.useRef(expandedPanelOpen);
  const collapsedRouteItemIdsRef = React.useRef(collapsedRouteItemIds);
  expandedPanelOpenRef.current = expandedPanelOpen;
  collapsedRouteItemIdsRef.current = collapsedRouteItemIds;
  const [expandedPanelInteractionReady, setExpandedPanelInteractionReady] = React.useState(false);
  const [renderCollapsedContent, setRenderCollapsedContent] =
    React.useState(!sidebarLayoutExpanded);
  const [renderExpandedContent, setRenderExpandedContent] = React.useState(sidebarLayoutExpanded);
  const [sideNavHoverMenusSuppressed, suppressSideNavHoverMenusDuringToggle] = useTimedBooleanFlag(
    PROJECT_NAVBAR_HOVER_MENU_TOGGLE_SUPPRESSION_MS,
  );
  const handleSideNavItemBeforeSelect = React.useCallback(
    (item: SideNavV2Item<string>) => {
      if (hasSideNavOverride) return;

      const targetId = getSideNavPointerTargetId(item);
      const routeShouldLandCollapsed =
        collapsedRouteItemIdsRef.current.has(item.id) ||
        collapsedRouteItemIdsRef.current.has(targetId);
      const targetSidebarPrefersOpen = state.sidebarOpenPreference ?? state.sidebarOpen;
      if (!expandedPanelOpenRef.current) return;

      if (routeShouldLandCollapsed) {
        preserveHoverExpandedSidebarForNextRoute({ collapseOnDestination: true });
        return;
      }

      preserveHoverExpandedSidebarForNextRoute();

      if (routeRequestsCollapsedSidebar && targetSidebarPrefersOpen) {
        preserveOpenSidebarForNextRoute();
      }
    },
    [
      hasSideNavOverride,
      routeRequestsCollapsedSidebar,
      state.sidebarOpen,
      state.sidebarOpenPreference,
    ],
  );
  const handleSideNavItemSelect = useCallback((item: SideNavV2Item<string>) => {
    setOptimisticActiveItemId(getSideNavPointerTargetId(item));
  }, []);

  const collapseSidebar = useCallback(() => {
    if (forceCollapsed) return;

    suppressSideNavHoverMenusDuringToggle();
    setRenderCollapsedContent(true);
    setCollapsedRailHovered(false);
    setCollapsedRailHoverSuppressed(true);
    setUserSidebarOpen(false);
  }, [forceCollapsed, setUserSidebarOpen, suppressSideNavHoverMenusDuringToggle]);

  const expandSidebar = useCallback(() => {
    if (forceCollapsed) return;

    suppressSideNavHoverMenusDuringToggle();
    setRenderExpandedContent(true);
    setCollapsedRailHovered(false);
    setCollapsedRailHoverSuppressed(false);
    setUserSidebarOpen(true);
  }, [forceCollapsed, setUserSidebarOpen, suppressSideNavHoverMenusDuringToggle]);

  useLayoutEffect(() => {
    setOptimisticActiveItemId(undefined);
  }, [routeActiveItemId]);

  useEffect(() => {
    hasCommittedRef.current = true;
    hasMountedProjectNavbarV2 = true;
  }, []);

  React.useEffect(() => {
    if (!hoverExpandedSidebarRouteHandoffOnMount.restore) return;

    clearHoverExpandedSidebarRouteHandoff();
    if (!hoverExpandedSidebarRouteHandoffOnMount.collapseOnDestination) return;

    const animationFrameId = window.requestAnimationFrame(() => {
      setCollapsedRailHovered(false);
      setCollapsedRailHoverSuppressed(true);
    });

    return () => window.cancelAnimationFrame(animationFrameId);
  }, [hoverExpandedSidebarRouteHandoffOnMount]);

  React.useEffect(() => {
    if (!shouldDeferDefaultCollapse) return;

    const animationFrameId = window.requestAnimationFrame(() => {
      setCollapsedRailHovered(false);
      setCollapsedRailHoverSuppressed(false);
      updateState({ sidebarOpen: false });
      setDefaultCollapsedAppliedRouteKey(defaultCollapsedRouteKey);
    });

    return () => window.cancelAnimationFrame(animationFrameId);
  }, [defaultCollapsedRouteKey, shouldDeferDefaultCollapse, updateState]);

  useEffect(() => {
    if (!routeRequestsCollapsedSidebar) {
      if (defaultCollapsedAppliedRouteKey != null) {
        setDefaultCollapsedAppliedRouteKey(undefined);
      }
      return;
    }

    if (!applyingDefaultCollapsed) return;

    if (!hoverExpandedSidebarRouteHandoffOnMount.restore) {
      setCollapsedRailHovered(false);
    }
    setCollapsedRailHoverSuppressed(false);
    updateState({ sidebarOpen: false });
    setDefaultCollapsedAppliedRouteKey(defaultCollapsedRouteKey);
  }, [
    applyingDefaultCollapsed,
    defaultCollapsedAppliedRouteKey,
    defaultCollapsedRouteKey,
    routeRequestsCollapsedSidebar,
    hoverExpandedSidebarRouteHandoffOnMount.restore,
    updateState,
  ]);

  React.useEffect(() => {
    if (!sidebarLayoutExpanded) {
      setRenderCollapsedContent(true);
      const timeoutId = window.setTimeout(() => {
        setRenderExpandedContent(false);
      }, PROJECT_NAVBAR_CONTENT_TRANSITION_MS);

      return () => window.clearTimeout(timeoutId);
    }

    setRenderExpandedContent(true);
    const timeoutId = window.setTimeout(() => {
      setRenderCollapsedContent(false);
    }, PROJECT_NAVBAR_CONTENT_TRANSITION_MS);

    return () => window.clearTimeout(timeoutId);
  }, [sidebarLayoutExpanded]);

  useEffect(() => {
    if (projectId == null || environmentId == null) return;

    setNavigationV2AppReturnPath(Routes.project.environment.root(projectId, environmentId));
  }, [environmentId, projectId]);

  React.useEffect(() => {
    if (!expandedPanelOpen) {
      setExpandedPanelInteractionReady(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setExpandedPanelInteractionReady(true);
    }, PROJECT_NAVBAR_WIDTH_TRANSITION_MS);

    return () => window.clearTimeout(timeoutId);
  }, [expandedPanelOpen]);

  useEffect(() => {
    if (!collapsedRailHoverSuppressed) return;

    const timeoutId = window.setTimeout(() => {
      setCollapsedRailHoverSuppressed(false);
    }, PROJECT_NAVBAR_COLLAPSE_HOVER_SUPPRESSION_MS);

    return () => window.clearTimeout(timeoutId);
  }, [collapsedRailHoverSuppressed]);

  const showCollapsedRailHoverPanel = useCallback(() => {
    if (collapsedRailHoverSuppressed || collapsedRailHovered) return;

    setRenderExpandedContent(true);
    setCollapsedRailHovered(true);
  }, [collapsedRailHoverSuppressed, collapsedRailHovered]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        forceCollapsed ||
        event.defaultPrevented ||
        !isSidebarToggleShortcut(event) ||
        isEditableKeyboardTarget(event.target)
      ) {
        return;
      }

      event.preventDefault();

      if (sidebarLayoutExpanded) {
        collapseSidebar();
        return;
      }

      expandSidebar();
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [collapseSidebar, expandSidebar, forceCollapsed, sidebarLayoutExpanded]);

  const sideNav = (
    <SideNavV2
      key={sidebarLayoutExpanded ? "expanded" : "collapsed"}
      workspaceLabel={effectiveWorkspaceLabel}
      sections={effectiveSideNavSections}
      activeItemId={activeItemId}
      onBeforeItemSelect={handleSideNavItemBeforeSelect}
      onItemSelect={handleSideNavItemSelect}
      workspaceMenuItems={effectiveWorkspaceMenuItems}
      onToggleCollapse={expandedPanelOpen ? expandSidebar : collapseSidebar}
      showToggleButton
      showDetailPanes={false}
      scrollPositionKey={sideNavScrollPositionKey}
      currentPath={router.asPath}
      hoverMenusDisabled={sideNavHoverMenusSuppressed || !sidebarLayoutExpanded}
      hoverMenuOpenDelayMs={
        expandedPanelOpen && !expandedPanelInteractionReady ? PROJECT_NAVBAR_WIDTH_TRANSITION_MS : 0
      }
      toggleButtonIcon={expandedPanelOpen ? "panelLeftDashed" : "panelLeft"}
      toggleButtonLabel={expandedPanelOpen ? "Lock sidebar open" : "Hide sidebar"}
      toggleTooltipLabel={expandedPanelOpen ? "Lock" : "Hide"}
      toggleTooltipShortcut={sidebarToggleTooltipShortcut}
      themeMode={themeMode}
      className={"h-full !w-full !rounded-none bg-cds-surface-active dark:bg-cds-inset-dark"}
      footerAccessory={<ProjectNavbarAdminModeControl />}
      aria-label={sideNavOverride?.ariaLabel}
    />
  );

  return (
    <div
      className={classNames(
        "sticky top-0 h-screen overflow-visible ease-out",
        `transition-[width] ${MOTION_DURATION_CLASS.medium}`,
        ZIndexClass.NAVIGATION_CHROME,
        "bg-cds-surface-active dark:bg-cds-inset-dark",
      )}
      style={{
        width: sidebarLayoutExpanded
          ? PROJECT_NAVBAR_DEFAULT_WIDTH_PX
          : PROJECT_NAVBAR_COLLAPSED_WIDTH_PX,
      }}
      onMouseLeave={() => {
        if (!sidebarCollapsed) return;

        setCollapsedRailHoverSuppressed(false);
        setCollapsedRailHovered(false);
      }}
    >
      <div className={"relative h-full"}>
        {renderCollapsedContent && (
          <div
            aria-hidden={sidebarLayoutExpanded}
            className={classNames(
              PROJECT_NAVBAR_CONTENT_SWAP_CLASS,
              "w-10",
              !sidebarLayoutExpanded
                ? "translate-x-0 opacity-100"
                : "pointer-events-none translate-x-cds-2 opacity-0",
            )}
          >
            <ProjectCollapsedSideNavV2
              workspaceLabel={effectiveWorkspaceLabel}
              sections={effectiveSideNavSections}
              activeItemId={activeItemId}
              scrollPositionKey={sideNavScrollPositionKey}
              onItemSelect={handleSideNavItemSelect}
              onMouseEnter={showCollapsedRailHoverPanel}
              onMouseMove={showCollapsedRailHoverPanel}
              themeMode={themeMode}
            />
          </div>
        )}
        {renderExpandedContent && (
          <div
            aria-hidden={!sidebarLayoutExpanded}
            className={classNames(
              PROJECT_NAVBAR_CONTENT_SWAP_CLASS,
              "w-full",
              sidebarLayoutExpanded
                ? "translate-x-0 opacity-100"
                : "pointer-events-none -translate-x-cds-2 opacity-0",
            )}
          >
            {sideNav}
          </div>
        )}
      </div>
    </div>
  );
};

const ProjectCollapsedSideNavV2: React.FC<{
  workspaceLabel: string;
  sections: readonly SideNavV2Section<string>[];
  activeItemId?: string;
  scrollPositionKey?: string;
  onItemSelect?: (item: SideNavV2Item<string>) => void;
  onMouseEnter?: MouseEventHandler<HTMLElement>;
  onMouseMove?: MouseEventHandler<HTMLElement>;
  onMouseLeave?: MouseEventHandler<HTMLElement>;
  themeMode?: "light" | "dark";
}> = ({
  workspaceLabel,
  sections,
  activeItemId,
  scrollPositionKey,
  onItemSelect,
  onMouseEnter,
  onMouseMove,
  onMouseLeave,
  themeMode,
}) => {
  const redSurfacesSuppressed = useRedSurfacesSuppressed();
  const isDarkMode = themeMode !== "light";
  const themeClass =
    themeMode === "dark" ? "dark" : themeMode === "light" ? "theme-light" : undefined;
  const scrollTop = usePersistentScrollTop(scrollPositionKey);
  const scrollViewportRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const node = scrollViewportRef.current;
    if (node == null || node.scrollTop === scrollTop) return;

    node.scrollTop = scrollTop;
  }, [activeItemId, scrollTop, sections]);

  return (
    <nav
      aria-label={"Collapsed primary navigation"}
      data-ds-v2={""}
      className={classNames(
        themeClass,
        "relative h-full w-10 overflow-hidden",
        "bg-cds-surface-active dark:bg-cds-inset-dark",
        isDarkMode ? "text-zinc-400" : "text-cds-ink-subtle",
      )}
      onMouseEnter={onMouseEnter}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      onClickCapture={preventDefaultAndStopPropagation}
    >
      <div
        className={classNames(
          "absolute left-0 top-0 z-10 flex h-cds-40 w-full items-start justify-center",
          "bg-cds-surface-active pt-cds-6 dark:bg-cds-inset-dark",
          scrollTop > 0 && "shadow-cds-s dark:shadow-cds-m-dark",
        )}
      >
        <div className={"flex h-7 w-full items-center justify-center"}>
          <SideNavWorkspaceAvatarV2 label={workspaceLabel} themeMode={themeMode} />
        </div>
      </div>
      <div ref={scrollViewportRef} role={"list"} className={"absolute inset-0 overflow-hidden"}>
        <div className={"flex w-full flex-col items-center pt-cds-40"}>
          <div className={"flex w-[30px] flex-col gap-cds-2"}>
            {sections.map((section) => {
              if (section.hidden === true) return null;

              return (
                <div key={section.id} role={"presentation"} className={"flex flex-col gap-cds-2"}>
                  {section.label && (
                    <div
                      aria-hidden={true}
                      className={"flex h-7 w-[30px] items-center justify-center"}
                    >
                      <span
                        className={classNames(
                          "h-px w-cds-12 rounded-full",
                          isDarkMode ? "bg-white/[0.14]" : "bg-zinc-300/80",
                        )}
                      />
                    </div>
                  )}
                  {section.items.map((item) => {
                    const selectable = item.deprecationNotice == null;
                    const active = selectable && item.id === activeItemId;
                    const childActive = selectable && sideNavItemContainsId(item, activeItemId);
                    const selectedNavigationSuppressed =
                      (active || childActive) && item.allowNavigationWhenSelected === false;
                    const children = item.children ?? [];
                    const inlineChildrenVisible =
                      children.length > 0 &&
                      item.childrenDisplay !== "menu" &&
                      (active || childActive);

                    return (
                      <div
                        key={item.id}
                        role={"listitem"}
                        className={classNames(
                          "flex flex-col",
                          inlineChildrenVisible && "gap-cds-2",
                        )}
                      >
                        <button
                          type={"button"}
                          aria-label={item.label}
                          aria-current={active || childActive ? "page" : undefined}
                          aria-disabled={item.deprecationNotice != null || undefined}
                          disabled={item.disabled || item.deprecationNotice != null}
                          className={classNames(
                            "flex h-7 w-[30px] items-center justify-center rounded-cds-6",
                            "transition-colors duration-75",
                            selectedNavigationSuppressed ? "cursor-default" : "cursor-pointer",
                            isDarkMode
                              ? "text-zinc-400 hover:bg-white/[0.08] hover:text-zinc-50"
                              : "text-cds-ink-subtle hover:!bg-zinc-100 hover:text-cds-ink-strong",
                            "focus-visible:outline-none focus-visible:!shadow-cds-focus",
                            active &&
                              (isDarkMode
                                ? "bg-white/[0.12] !text-white hover:bg-white/[0.12] [&_svg]:!text-white"
                                : "!bg-zinc-200 text-cds-ink-strong hover:!bg-zinc-200"),
                            !active &&
                              childActive &&
                              (isDarkMode
                                ? "bg-white/[0.06] !text-white hover:bg-white/[0.06] [&_svg]:!text-white"
                                : "!bg-zinc-200/60 text-cds-ink-strong hover:!bg-zinc-200/60"),
                            item.visualTone === "danger" &&
                              !redSurfacesSuppressed &&
                              CHALK_DANGER_SURFACE_CLASS,
                            item.admin &&
                              item.visualTone !== "danger" &&
                              !redSurfacesSuppressed &&
                              CHALK_ADMIN_SURFACE_CLASS,
                          )}
                          onClick={() => {
                            if (
                              item.disabled ||
                              item.deprecationNotice != null ||
                              selectedNavigationSuppressed
                            ) {
                              return;
                            }
                            item.onSelect?.();
                            onItemSelect?.(item);
                          }}
                        >
                          {item.icon && <Icon name={item.icon} size={"md"} aria-hidden={true} />}
                        </button>
                        {inlineChildrenVisible && (
                          <div aria-hidden={true} className={"flex w-[30px] flex-col gap-cds-2"}>
                            {children.map((child) => {
                              const childSelected =
                                child.id === activeItemId ||
                                sideNavItemContainsId(child, activeItemId);

                              return (
                                <div
                                  key={child.id}
                                  className={"flex h-7 w-[30px] items-center justify-center"}
                                >
                                  <span
                                    className={classNames(
                                      "flex h-cds-24 w-cds-24 items-center justify-center rounded-cds-6",
                                      childSelected
                                        ? isDarkMode
                                          ? "bg-white/[0.12]"
                                          : "bg-zinc-200"
                                        : "bg-cds-surface-active dark:bg-cds-inset-dark",
                                      child.visualTone === "danger" &&
                                        !redSurfacesSuppressed &&
                                        CHALK_DANGER_SURFACE_CLASS,
                                      child.admin &&
                                        child.visualTone !== "danger" &&
                                        !redSurfacesSuppressed &&
                                        CHALK_ADMIN_SURFACE_CLASS,
                                    )}
                                  >
                                    {child.icon ? (
                                      <Icon
                                        name={child.icon}
                                        size={"sm"}
                                        aria-hidden={true}
                                        className={classNames(
                                          "shrink-0 transition-colors duration-75",
                                          isDarkMode
                                            ? childSelected
                                              ? "!text-white"
                                              : "!text-zinc-400"
                                            : childSelected
                                              ? "!text-zinc-800"
                                              : "!text-cds-ink-subtle",
                                        )}
                                      />
                                    ) : (
                                      <span
                                        className={classNames(
                                          "h-cds-4 w-cds-4 rounded-full transition-colors duration-75",
                                          isDarkMode
                                            ? childSelected
                                              ? "bg-white"
                                              : "bg-zinc-600"
                                            : childSelected
                                              ? "bg-zinc-800"
                                              : "bg-zinc-300",
                                        )}
                                      />
                                    )}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
          <div aria-hidden={true} className={"flex shrink-0 flex-col"}>
            <span className={"h-cds-40"} />
            <span className={"h-cds-40"} />
            <span className={"h-cds-16"} />
          </div>
        </div>
      </div>
      <div
        className={classNames(
          "absolute bottom-0 left-0 z-10 flex w-full justify-center py-cds-6",
          "bg-cds-surface-active dark:bg-cds-inset-dark",
        )}
      >
        <CollapsedNavbarAdminIndicators />
      </div>
    </nav>
  );
};
